import type { Transcript } from 'src/types/script';
import type { IFileItem, IIntentItem } from 'src/types/file';
import type { NavProps, NavItemProps } from 'src/components/navbar';

import { scroller } from 'react-scroll';

import {
  type Data,
  type DemographicInfo,
  type IComplianceBorrower,
  EComplianceBorrowerFieldKey,
} from 'src/types/summary';

import { FAILED_SUMMARY_STATUSES } from './constants';
import {
  CallStatus,
  UploadStatus,
  PipelineType,
  RunConfigStatus,
  ApiSummaryStatus,
} from './enumerations';

interface FileValidationParams {
  pipelineType: PipelineType;
  audioFilesCount: number;
  pdfFilesCount: number;
  isPdfOnly?: boolean;
}

export const getTranscriptFeedbackValue = (transcript: Transcript): string => {
  if (!transcript) return '';
  if (typeof transcript === 'string') return transcript;
  return `${transcript.timestamp} - ${transcript.content}`;
};

export const mapSummaryStatus = (status: string) => {
  if (status === ApiSummaryStatus.SummarizationSavedOnline) {
    return CallStatus.Complete;
  }

  if (status === ApiSummaryStatus.SpeakerDiarizationComplete) {
    return CallStatus.Finalizing;
  }

  if (FAILED_SUMMARY_STATUSES.includes(status)) {
    return CallStatus.Failed;
  }

  return CallStatus.Processing;
};

/**
 * Extracts file information from an array of file objects and maps it to an array of IFileItem objects.
 *
 * @param files - An array of file objects to be mapped.
 * @returns An array of IFileItem objects with additional properties.
 */
export const mapDataToIFileItem = (files: any[]): IFileItem[] =>
  files.map((file) => ({
    ...file,
    created: new Date(`${file.timestamp}Z`), // Z added for explicit UTC, to convert to local time
    name: file.name,
    size: file.size,
    callId: file.call?.id,
    callStatus: file.call ? mapSummaryStatus(file.call.status) : CallStatus.Failed,
    callUpdated: file.call ? new Date(`${file.call.updated}Z`) : null, // Z added for explicit UTC, to convert to local time
  }));

export const getYesNoValue = (value: string) => {
  if (value === null) return 'N/A';
  switch (value?.toUpperCase()) {
    case 'Y':
      return 'Yes';
    case 'N':
      return 'No';
    default:
      return capitalizeFirstLetter(value);
  }
};

export const capitalizeFirstLetter = (string: string) => {
  if (!string) return string;
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const getFileErrorMessage = (status: UploadStatus): string => {
  switch (status) {
    case UploadStatus.GeneralError:
      return 'File upload failed.';
    case UploadStatus.FileSizeError:
      return 'File size exceeds 200 MB limit.';
    case UploadStatus.FileTypeError:
      return 'Invalid file type. Supported types: WAV, MP3, MP4.';
    default:
      return '';
  }
};

export const getRefetchFileTime = (size: number): number => {
  const mb = 1024 * 1024;
  const min = 60 * 1000;

  if (size < 10 * mb) {
    // size < 10MB
    return 1 * min; // 1 minute in milliseconds
  }
  if (size >= 10 * mb && size < 50 * mb) {
    // 10MB <= size < 50MB
    return 2 * min; // 2 minutes in milliseconds
  }
  if (size >= 50 * mb && size < 100 * mb) {
    // 50MB <= size < 100MB
    return 4 * min; // 4 minutes in milliseconds
  }
  // size >= 100MB
  return 5 * min; // 5 minutes in milliseconds
};

export const isTimeTwentyMinutesOld = (dateTime: Date): boolean => {
  const now = new Date();
  const twentyMinutesAgo = new Date(now.getTime() - 20 * 60 * 1000);
  return dateTime <= twentyMinutesAgo;
};

export const getSetOfSectionIds = (sections: any): Set<string> => {
  const _sections = new Set<string>();
  sections.map((section: { id: string }) => _sections.add(section.id));
  return _sections;
};

export const scrollToElement = (id: string) => {
  scroller.scrollTo(id, {
    offset: -80,
    smooth: true,
  });
};

export const getDownloadFileName = (baseName: string, format: string): string => {
  const timestamp = new Date().getTime();
  return `${baseName}_${timestamp}${format}`;
};

/**
 * Filters the navigation data based on the user's roles.
 *
 * @param navData - The original navigation data.
 * @param userRoles - The roles of the user.
 * @returns The filtered navigation data containing only the items that match the user's roles.
 */
export const getFilteredNavData = (
  navData: NavProps['data'],
  userRoles: string[]
): NavProps['data'] => [
  {
    subheader: '',
    items: navData[0].items.filter((item: NavItemProps) =>
      item.id ? userRoles.includes(item.id) : false
    ),
  },
];

export const getPdfFileUploadErrorMessage = (code: string): string => {
  switch (code) {
    case 'too-many-files':
      return 'Only one PDF file is allowed.';
    case 'file-too-large':
      return 'File size exceeds 200 MB limit.';
    case 'file-invalid-type':
      return 'Invalid file type. only PDF accepted.';
    default:
      return '';
  }
};

export const getAudioFileUploadErrorMessage = (code: string): string => {
  switch (code) {
    case 'file-too-large':
      return 'File size exceeds 200 MB limit.';
    case 'file-invalid-type':
      return 'Invalid file type. Supported types: WAV, MP3, MP4.';
    default:
      return '';
  }
};

/**
 * Returns the field value for a borrower from the form_1003 data
 * Updated to handle array fields (ethnicity, race)
 */
export const getBorrowerFieldValue = (
  borrower: IComplianceBorrower | null,
  fieldKey: EComplianceBorrowerFieldKey,
  index?: number
): Data => {
  const fallbackValue: Data = { value: '-', __path: '' };
  if (!borrower) return fallbackValue;

  // Handle name field
  if (fieldKey === EComplianceBorrowerFieldKey.NAME) {
    return borrower.borrower_name || fallbackValue;
  }

  // Handle missing field
  if (!borrower[fieldKey]) return fallbackValue;

  // Handle array fields (ethnicity and race)
  if (Array.isArray(borrower[fieldKey])) {
    const infoArray = borrower[fieldKey] as DemographicInfo[];

    // Return specific index if provided
    if (index !== undefined && infoArray[index]) {
      return infoArray[index].form_1003 || fallbackValue;
    }

    // Otherwise return first item (backwards compatibility)
    return infoArray[0]?.form_1003 || fallbackValue;
  }

  // Handle non-array fields (sex)
  return (borrower[fieldKey] as DemographicInfo).form_1003 || fallbackValue;
};

export const mapRunConfigStatus = (status: string) => {
  if (status === 'INPROGRESS') {
    return RunConfigStatus.InProgress;
  }

  if (status === 'COMPLETED') {
    return RunConfigStatus.Completed;
  }

  if (status === 'FAILED') {
    return RunConfigStatus.Failed;
  }

  return RunConfigStatus.Pending;
};

export const getRunConfigId = (intent: any) => {
  if (intent.pipeline_type === PipelineType.Servicing) {
    return intent.run_config ? intent.run_config.id : null;
  }
  return intent.run_config ? intent.run_config.id : null;
};

/**
 * Maps data to an array of IIntentItem objects.
 *
 * @param data - An array of data objects to be mapped.
 * @returns An array of IIntentItem objects with additional properties.
 */
export const mapDataToIIntentItem = (data: any[]): IIntentItem[] =>
  data.map((intent) => {
    const audioFile = intent.files.find((file: any) => file.extension !== 'pdf');
    const pdfFile = intent.files.find((file: any) => file.extension === 'pdf');

    return {
      id: intent.id,
      audioFileName: audioFile ? audioFile.name : '',
      audioFileId: audioFile ? audioFile.id : '',
      pdfFileName: pdfFile ? pdfFile.name : null,
      pdfFileId: pdfFile ? pdfFile.id : null,
      created: new Date(`${intent.created_at}Z`),
      runConfigUpdated: intent.run_config
        ? new Date(`${intent.run_config.updated_at}Z`)
        : new Date(`${intent.created_at}Z`), // use the intent created at as a fallback
      runConfigId: getRunConfigId(intent),
      runConfigStatus: (() => {
        if (
          intent.pipeline_type === PipelineType.Servicing ||
          intent.pipeline_type === PipelineType.Sales
        ) {
          return intent.run_config
            ? mapRunConfigStatus(intent.run_config.status)
            : RunConfigStatus.InProgress;
        }
        return pdfFile && intent.run_config
          ? mapRunConfigStatus(intent.run_config.status)
          : RunConfigStatus.Pending;
      })(),
      audioFileSize: audioFile ? audioFile.size : 0,
      pipelineType: intent.pipeline_type,
      lastName: '', // Assuming this value will be added later
    };
  });

/**
 * Validates if the files are valid to upload based on pipeline type
 * @param pipelineType - The type of pipeline being used
 * @param audioFilesCount - Number of audio files to upload
 * @param pdfFilesCount - Number of PDF files to upload
 * @param isPdfOnly - Whether only PDF upload is allowed (with existing audio reference)
 * @returns boolean indicating if the files are valid to upload
 */
export const areFilesValidToUpload = ({
  pipelineType,
  audioFilesCount,
  pdfFilesCount,
  isPdfOnly = false,
}: FileValidationParams): boolean => {
  if (pipelineType === PipelineType.Hmda) {
    return (
      (audioFilesCount === 1 && pdfFilesCount === 1) ||
      (audioFilesCount >= 1 && pdfFilesCount === 0) ||
      (pdfFilesCount === 1 && isPdfOnly)
    );
  }
  // For Servicing and Sales, just check for audio files
  return audioFilesCount >= 1;
};

export const getArrayItemLabel = (baseLabel: string, index: number): string =>
  index === 0 ? baseLabel : `${baseLabel} Sub`;

export interface CopilotMessage {
  role: string;
  content: {
    type: string;
    [key: string]: string | string[];
  }[];
}
